/** Shared native/Searchanise recovery action. No stock writes or cart events.
 * Keep the product-page fallback until STOQ is ready. Verify current Shopify
 * availability on click so stale search indexes cannot hide purchasable options.
 */
(() => {
  'use strict';
  if (window.ETCardRestock) return;
  const selector = '[data-etr-card-restock]';
  const label = document.currentScript?.dataset.notifyLabel || 'Notify me';
  const originals = new WeakMap();
  const pending = new WeakSet();
  let enabled = true;
  let attempts = 0;
  let timer;
  let scriptRequested = false;
  const ready = () => enabled && typeof window._RestockRocket?.openModal === 'function';

  function restore(button) {
    const original = originals.get(button);
    if (!original) return;
    button.replaceChildren(...original.nodes.map(node => node.cloneNode(true)));
    for (const [name, value] of Object.entries(original.attributes)) {
      if (value === null) button.removeAttribute(name);
      else button.setAttribute(name, value);
    }
    button.removeAttribute('data-etr-restock-ready');
    originals.delete(button);
  }

  // The embed exposes its versioned collection build even when automatic list
  // buttons are disabled. Load that same build for this custom integration only
  // after enabled settings and an eligible card exist; leave app settings intact.
  function ensureApp() {
    const config = window._RestockRocketConfig;
    if (ready() || scriptRequested || !enabled || config?.settings?.enable_app !== true || !document.querySelector(selector)) return;
    const source = config.settings.preorder_enabled ? config.scriptUrlCollection : config.scriptUrlCollectionBis;
    if (!source) return;
    const url = new URL(source, location.href);
    if (url.origin !== 'https://cdn.shopify.com' || !url.pathname.startsWith('/extensions/')) return;
    scriptRequested = true;
    if ([...document.scripts].some(script => script.src === url.href)) return;
    const script = document.createElement('script');
    script.src = url.href;
    script.defer = true;
    script.addEventListener('load', () => { attempts = 0; awaitApp(); });
    document.body.append(script);
  }

  function enhance(button) {
    ensureApp();
    if (!ready() || !/^\d+$/.test(button.dataset.etrCardRestock || '') || button.hasAttribute('data-etr-restock-ready')) return;
    const attributes = {};
    for (const name of ['aria-label', 'aria-haspopup', 'role']) attributes[name] = button.getAttribute(name);
    originals.set(button, { nodes: [...button.childNodes].map(node => node.cloneNode(true)), attributes });
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS(svg.namespaceURI, 'path');
    path.setAttribute('d', 'M4 5h16v14H4z m0 1 8 6 8-6');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.8');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    const text = document.createElement('span');
    text.textContent = label;
    button.replaceChildren(svg, text);
    button.setAttribute('aria-label', `${label}: ${button.dataset.etrRestockTitle || button.closest('.t4s-product')?.querySelector('.t4s-product-title')?.textContent.trim() || ''}`);
    button.setAttribute('aria-haspopup', 'dialog');
    if (button.tagName === 'A') button.setAttribute('role', 'button');
    button.setAttribute('data-etr-restock-ready', '');
  }

  function scan(root) {
    if (root.nodeType !== 1 && root !== document) return;
    if (root.matches?.(selector)) enhance(root);
    root.querySelectorAll(selector).forEach(enhance);
  }

  // Readiness polling is bounded; the app event handles later asynchronous loads.
  function awaitApp() {
    clearTimeout(timer);
    if (!enabled) {
      document.querySelectorAll('[data-etr-restock-ready]').forEach(restore);
      return;
    }
    ensureApp();
    if (ready()) scan(document);
    else if (++attempts < 40) timer = setTimeout(awaitApp, 250);
  }
  window.ETCardRestock = { enhance, restore };
  window.addEventListener('stoq:loaded', event => {
    enabled = event.detail?.enabled !== false;
    attempts = 0;
    awaitApp();
  });
  new MutationObserver(records => {
    ensureApp();
    if (!ready()) return;
    records.forEach(record => record.addedNodes.forEach(scan));
  }).observe(document.body, { childList: true, subtree: true });

  async function open(button) {
    if (pending.has(button)) return;
    pending.add(button);
    button.setAttribute('aria-busy', 'true');
    const href = button.dataset.etrRestockUrl || button.getAttribute('href');
    const url = new URL(href, location.href);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Unverified product URL');
      const endpoint = new URL(url.href);
      endpoint.pathname = endpoint.pathname.replace(/\/$/, '') + '.js';
      endpoint.search = '';
      endpoint.hash = '';
      const response = await fetch(endpoint.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Product could not be verified');
      const product = await response.json();
      if (String(product.id) !== button.dataset.etrCardRestock || product.available !== false ||
          !Array.isArray(product.variants) || !product.variants.length ||
          !product.variants.every(variant => variant.available === false && /^\d+$/.test(String(variant.id))) || !ready()) {
        throw new Error('Availability changed or restock app unavailable');
      }
      // Pass every authentic variant so STOQ offers the correct size/flavour picker.
      await window._RestockRocket.openModal(product, String(product.variants[0].id));
    } catch (_) {
      // An app/network failure must leave a useful path to the product page.
      location.assign(url.href);
    } finally {
      clearTimeout(timeout);
      pending.delete(button);
      button.removeAttribute('aria-busy');
    }
  }

  // Capture before Searchanise's delegated navigation handler; do not intercept
  // modified clicks or purchase actions belonging to Shopify/app cart handlers.
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-etr-restock-ready]');
    if (!button || !ready() || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    open(button);
  }, true);
  document.addEventListener('keydown', event => {
    const button = event.target.closest?.('a[data-etr-restock-ready]');
    if (button && event.key === ' ' && ready()) {
      event.preventDefault();
      open(button);
    }
  });
  awaitApp();
})();
